import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TChannel } from '@feathr/blackbox';
import { Button, Modal } from '@feathr/components';

import type { TCreativeType } from '../creatives.types';
import DraftCampaignSelect from '../DuplicateCreativeModal/DraftCampaignSelect';
import type { TAddCreativeStep } from './AddCreativeFlow.hooks';
import { useAddCreativeFlow } from './AddCreativeFlow.hooks';
import ChooseChannelContent from './ChooseChannelContent';
import ChooseCreativeTypeContent from './ChooseCreativeTypeContent';

import * as styles from './AddCreativeFlow.css';

export interface IAddCreativeFlowProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Callback when flow completes with selected channel, creative type, and optional campaignId */
  onComplete: (channel: TChannel, creativeType: TCreativeType, campaignId?: string) => void;
  /** Initial step to show (defaults to 'channel') */
  initialStep?: TAddCreativeStep;
  /** Initial channel selection (used with initialStep='creative-type') */
  initialChannel?: TChannel;
  /** Available channels based on campaign selection. If single channel, auto-selects and skips step 1. */
  availableChannels?: TChannel[];
  /** Event ID for filtering campaigns in campaign selection step */
  eventId?: string;
  /** Campaign ID when initiated from campaign context (skips campaign step) */
  campaignId?: string;
}

function AddCreativeFlow({
  isOpen,
  onClose,
  onComplete,
  initialStep,
  initialChannel,
  availableChannels,
  eventId,
  campaignId,
}: Readonly<IAddCreativeFlowProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    step,
    selectedChannel,
    selectedCreativeType,
    handleChannelSelect,
    handleCreativeTypeSelect,
    handleCampaignSelect,
    handleNextFromChannel,
    handleNextFromCreativeType,
    handleNextFromCampaign,
    handleBack,
    handleClose,
    isNextDisabledOnChannel,
    isNextDisabledOnCreativeType,
    modalTitle,
    modalDescription,
    creativeTypeOptions,
    shouldHideBackButton,
    eventId: hookEventId,
  } = useAddCreativeFlow({
    onClose,
    onComplete,
    initialStep,
    initialChannel,
    availableChannels,
    eventId,
    campaignId,
  });

  const showBackButton = (step === 'creative-type' && !shouldHideBackButton) || step === 'campaign';

  const leftActions = showBackButton ? (
    <Button key={'back'} name={'back'} onClick={handleBack}>
      {t('Back')}
    </Button>
  ) : undefined;

  const rightActions = useMemo<JSX.Element[]>(() => {
    const cancelButton = (
      <Button key={'cancel'} name={'cancel'} onClick={handleClose}>
        {t('Cancel')}
      </Button>
    );
    if (step === 'channel') {
      return [
        cancelButton,
        <Button
          disabled={isNextDisabledOnChannel}
          key={'next'}
          name={'next'}
          onClick={handleNextFromChannel}
          type={'primary'}
        >
          {t('Next')}
        </Button>,
      ];
    }
    if (step === 'campaign') {
      return [
        cancelButton,
        <Button key={'next'} name={'next'} onClick={handleNextFromCampaign} type={'primary'}>
          {t('Next')}
        </Button>,
      ];
    }
    return [
      cancelButton,
      <Button
        disabled={isNextDisabledOnCreativeType}
        key={'next'}
        name={'next'}
        onClick={handleNextFromCreativeType}
        type={'primary'}
      >
        {t('Next')}
      </Button>,
    ];
  }, [
    step,
    handleClose,
    handleNextFromChannel,
    handleNextFromCampaign,
    handleNextFromCreativeType,
    isNextDisabledOnChannel,
    isNextDisabledOnCreativeType,
    t,
  ]);

  return (
    <Modal
      description={modalDescription}
      leftActions={leftActions}
      onClose={handleClose}
      opened={isOpen}
      rightActions={rightActions}
      title={modalTitle}
    >
      <div className={styles.container}>
        {step === 'channel' && (
          <ChooseChannelContent
            availableChannels={availableChannels}
            onChannelSelect={handleChannelSelect}
            selectedChannel={selectedChannel}
          />
        )}
        {step === 'creative-type' && selectedChannel && (
          <ChooseCreativeTypeContent
            onCreativeTypeSelect={handleCreativeTypeSelect}
            options={creativeTypeOptions}
            selectedCreativeType={selectedCreativeType}
          />
        )}
        {step === 'campaign' && selectedChannel && hookEventId && (
          <DraftCampaignSelect
            channel={selectedChannel}
            eventId={hookEventId}
            helpText={t('Select a draft campaign to add the creative to')}
            label={t('Campaign')}
            onChange={handleCampaignSelect}
            optional={true}
            placeholder={t('Select campaign')}
          />
        )}
      </div>
    </Modal>
  );
}

export default AddCreativeFlow;
